import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { AssetDto, AssetVersionDto, GalleryDto } from '@nusign/picflow-backend-types';
import { DataUnitsBigIntPipe } from '../../../shared/pipes/data-units-big-int.pipe';
import { AssetDownloadButtonComponent } from '../asset-download-button/asset-download-button.component';
import { defaultExtensionDataMap, extensionDataMap } from './non-preview-data';

@Component({
  selector: 'app-non-preview-file',
  imports: [DataUnitsBigIntPipe, AssetDownloadButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './non-preview-file.component.html',
  styleUrl: './non-preview-file.component.scss',
  host: {
    '[style.--icon-color]': `data()?.backgroundColor`,
    '[style.--background-color]': `backgroundColor()`,
    '[style.--text-color]': `textColor()`,
    '[style.--border-color]': `borderColor()`,
    '[style.--border-width]': `borderWidth()`,
  },
})
export class NonPreviewFileComponent {
  gallery = input.required<GalleryDto>();
  asset = input.required<AssetDto>();
  version = input<AssetVersionDto>(undefined);
  showSizeAndDownload = input<boolean>(false);
  showFilename = input<boolean>(false);
  darkBackground = input<boolean>(false);
  showBackground = input<boolean>(false);
  showBorder = input<boolean>(false);
  noDownload = input<boolean>(false);

  name = computed(() => {
    const asset = this.asset();
    if (!asset) return;
    return asset.name;
  });

  versionSize = computed(() => {
    const version = this.version();
    const asset = this.asset();
    if (!version && !asset) return;
    return (version || asset.version_data).size;
  });

  extension = computed(() => {
    const name = this.asset()?.version_data?.asset_name.toLowerCase();
    if (!name) return;
    const parts = name.split('.');
    return parts.length > 1 ? parts.pop() : '';
  });

  data = computed(() => {
    const extension = this.extension();
    const asset = this.asset();
    if (!extension || !asset) return;
    return (
      extensionDataMap.find(m => m.extensions.includes(extension)) ||
      defaultExtensionDataMap.find(m => m.type === asset.type)
    );
  });

  borderColor = computed(() => {
    return this.darkBackground() ? 'rgba(255,255,255,0.1)' : 'var(--color-grey-80a10)';
  });

  textColor = computed(() => {
    return this.darkBackground() ? 'white' : 'var(--color-grey-80)';
  });

  backgroundColor = computed(() => {
    const data = this.data();
    if (!data) return;
    return this.showBackground() ? data.backgroundColor : undefined;
  });

  borderWidth = computed(() => {
    return this.showBorder() ? '1px' : '0';
  });

  allowDownload = computed(() => {
    const gallery = this.gallery();
    const asset = this.asset();
    const noDownload = this.noDownload();
    if (!gallery || !asset) return false;
    if (noDownload) return false;
    if (gallery.permissions.can_edit) return true;
    if (!gallery.download_single_asset) return false;
    if (gallery.approved_downloads_only) {
      return asset.status === 'approved';
    }
    return true;
  });
}
